import React, { useState } from "react";
import { api } from "../lib/api";
import { Loader2, KeyRound, X } from "lucide-react";

export default function ChangePasswordModal({ open, onClose, isGoogleOnly = false }) {
    const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");

    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault();
        setErr(""); setMsg("");
        if (form.new_password.length < 8) return setErr("New password must be at least 8 characters.");
        if (form.new_password !== form.confirm) return setErr("New password and confirmation don't match.");
        setBusy(true);
        try {
            const payload = { new_password: form.new_password };
            if (!isGoogleOnly) payload.current_password = form.current_password;
            await api.post("/auth/change-password", payload);
            setMsg("Password updated successfully.");
            setForm({ current_password: "", new_password: "", confirm: "" });
            setTimeout(onClose, 900);
        } catch (e2) {
            setErr(e2?.response?.data?.detail || "Could not update password.");
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-card rounded-lg shadow-xl max-w-md w-full p-6 sm:p-8" onClick={e => e.stopPropagation()} data-testid="change-password-modal">
                <div className="flex items-start justify-between mb-5">
                    <div className="flex items-center gap-2">
                        <KeyRound className="h-5 w-5 text-primary" />
                        <h3 className="font-serif text-2xl font-bold">Change password</h3>
                    </div>
                    <button onClick={onClose} className="h-8 w-8 rounded-full hover:bg-accent flex items-center justify-center" aria-label="Close" data-testid="close-pw-modal">
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <form onSubmit={submit} className="space-y-4">
                    {!isGoogleOnly && (
                        <div>
                            <label className="ll-label block mb-1.5">Current password</label>
                            <input type="password" className="ll-input" required value={form.current_password}
                                   onChange={e => setForm({ ...form, current_password: e.target.value })}
                                   data-testid="current-password-input" />
                        </div>
                    )}
                    <div>
                        <label className="ll-label block mb-1.5">New password</label>
                        <input type="password" className="ll-input" required minLength={8} value={form.new_password}
                               onChange={e => setForm({ ...form, new_password: e.target.value })}
                               data-testid="new-password-input" />
                        <p className="text-[11px] text-muted-foreground mt-1">At least 8 characters. Use a mix of letters, numbers, and symbols.</p>
                    </div>
                    <div>
                        <label className="ll-label block mb-1.5">Confirm new password</label>
                        <input type="password" className="ll-input" required value={form.confirm}
                               onChange={e => setForm({ ...form, confirm: e.target.value })}
                               data-testid="confirm-password-input" />
                    </div>
                    {err && <div className="text-sm text-destructive" data-testid="pw-error">{err}</div>}
                    {msg && <div className="text-sm text-primary" data-testid="pw-success">{msg}</div>}
                    <div className="flex items-center gap-3 pt-1">
                        <button type="submit" disabled={busy}
                                className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60"
                                data-testid="submit-pw-btn">
                            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                            Update password
                        </button>
                        <button type="button" onClick={onClose} className="text-sm text-muted-foreground hover:text-foreground">Cancel</button>
                    </div>
                </form>
            </div>
        </div>
    );
}
